import styles from './CalendarCustomizeSelector.module.scss';
import headerActionStyles from '../HeaderActionButtons.module.scss';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal } from 'lucide-react';
import useOutsideClick from '../../../../../../utils/useOutsideClick';
import CalendarCustomizeContent from '../CalendarCustomizeContent';
import { useTranslation } from 'react-i18next';

const sameWorkTime = (a, b) =>
  Number(a?.hours) === Number(b?.hours) && Number(a?.minutes) === Number(b?.minutes);

const MENU_WIDTH = 360;
const MENU_GAP = 8;

export default ({
  title = 'Filtri',
  calendarWorkers,
  activeWorkerId,
  onActiveWorkerChange,
  daySelectedValues,
  numberOfActiveDays,
  onNumberOfActiveDaysChange,
  stepSize,
  onStepSizeChange,
  workTime: { setEndWorkTime, setStartWorkTime, startWorkTime, endWorkTime } = {
    setEndWorkTime: () => {},
    setStartWorkTime: () => {},
    startWorkTime: {},
    endWorkTime: {},
  },
  searchParams,
  hideClosedChairs = false,
  onHideClosedChairsChange,
  compactClosedDays = false,
  onCompactClosedDaysChange,
  showTaskIcons = true,
  onShowTaskIconsChange,
  showTaskTime = true,
  onShowTaskTimeChange,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});

  const [draftWorkerId, setDraftWorkerId] = useState(activeWorkerId);
  const [draftDays, setDraftDays] = useState(numberOfActiveDays);
  const [draftStepSize, setDraftStepSize] = useState(stepSize);
  const [draftStartWorkTime, setDraftStartWorkTime] = useState(startWorkTime);
  const [draftEndWorkTime, setDraftEndWorkTime] = useState(endWorkTime);
  const [draftHideClosedChairs, setDraftHideClosedChairs] = useState(hideClosedChairs);
  const [draftCompactClosedDays, setDraftCompactClosedDays] = useState(compactClosedDays);
  const [draftShowTaskIcons, setDraftShowTaskIcons] = useState(showTaskIcons);
  const [draftShowTaskTime, setDraftShowTaskTime] = useState(showTaskTime);

  const draftRef = useRef({});
  draftRef.current = {
    draftDays,
    draftStepSize,
    draftStartWorkTime,
    draftEndWorkTime,
    draftHideClosedChairs,
    draftCompactClosedDays,
    draftShowTaskIcons,
    draftShowTaskTime,
    numberOfActiveDays,
    stepSize,
    startWorkTime,
    endWorkTime,
    hideClosedChairs,
    compactClosedDays,
    showTaskIcons,
    showTaskTime,
    onNumberOfActiveDaysChange,
    onStepSizeChange,
    setStartWorkTime,
    setEndWorkTime,
    onHideClosedChairsChange,
    onCompactClosedDaysChange,
    onShowTaskIconsChange,
    onShowTaskTimeChange,
  };

  const syncDraftFromProps = useCallback(() => {
    setDraftWorkerId(activeWorkerId);
    setDraftDays(numberOfActiveDays);
    setDraftStepSize(stepSize);
    setDraftStartWorkTime(startWorkTime);
    setDraftEndWorkTime(endWorkTime);
    setDraftHideClosedChairs(hideClosedChairs);
    setDraftCompactClosedDays(compactClosedDays);
    setDraftShowTaskIcons(showTaskIcons);
    setDraftShowTaskTime(showTaskTime);
  }, [
    activeWorkerId,
    numberOfActiveDays,
    stepSize,
    startWorkTime,
    endWorkTime,
    hideClosedChairs,
    compactClosedDays,
    showTaskIcons,
    showTaskTime,
  ]);

  const commitDraft = useCallback(() => {
    const d = draftRef.current;
    if (d.draftDays !== d.numberOfActiveDays) d.onNumberOfActiveDaysChange?.(d.draftDays);
    if (d.draftStepSize !== d.stepSize) d.onStepSizeChange?.(d.draftStepSize);
    if (!sameWorkTime(d.draftStartWorkTime, d.startWorkTime)) {
      d.setStartWorkTime?.(d.draftStartWorkTime);
    }
    if (!sameWorkTime(d.draftEndWorkTime, d.endWorkTime)) {
      d.setEndWorkTime?.(d.draftEndWorkTime);
    }
    if (d.draftHideClosedChairs !== d.hideClosedChairs) {
      d.onHideClosedChairsChange?.(d.draftHideClosedChairs);
    }
    if (d.draftCompactClosedDays !== d.compactClosedDays) {
      d.onCompactClosedDaysChange?.(d.draftCompactClosedDays);
    }
    if (d.draftShowTaskIcons !== d.showTaskIcons) {
      d.onShowTaskIconsChange?.(d.draftShowTaskIcons);
    }
    if (d.draftShowTaskTime !== d.showTaskTime) {
      d.onShowTaskTimeChange?.(d.draftShowTaskTime);
    }
  }, []);

  const closeMenu = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const saveAndClose = useCallback(() => {
    commitDraft();
    closeMenu();
  }, [commitDraft, closeMenu]);

  const applyWorker = useCallback(
    (workerId) => {
      setDraftWorkerId(workerId);
      onActiveWorkerChange?.(workerId);
    },
    [onActiveWorkerChange],
  );

  const toggleDropdown = () => {
    if (isDropdownOpen) {
      closeMenu();
      return;
    }
    syncDraftFromProps();
    setIsDropdownOpen(true);
  };

  const updateMenuPosition = useCallback(() => {
    const trigger = dropdownRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(MENU_WIDTH, window.innerWidth - 16);
    const left = Math.min(
      Math.max(8, rect.right - width),
      Math.max(8, window.innerWidth - width - 8),
    );
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + MENU_GAP,
      left,
      width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isDropdownOpen) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isDropdownOpen, updateMenuPosition]);

  useOutsideClick(dropdownRef, (event) => {
    if (!isDropdownOpen) return;
    if (menuRef.current?.contains(event.target)) return;
    if (event?.target?.closest?.('select, option')) return;
    closeMenu();
  });

  useEffect(() => {
    if (!isDropdownOpen) syncDraftFromProps();
  }, [isDropdownOpen, syncDraftFromProps]);

  const stopScrollChain = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const menu = isDropdownOpen
    ? createPortal(
        <div ref={menuRef} className={styles.dropdown__menu} style={menuStyle} role="dialog">
          <div
            className={styles.dropdown__menu_scroll}
            onWheel={stopScrollChain}
            onTouchMove={stopScrollChain}
          >
            <CalendarCustomizeContent
              calendarWorkers={calendarWorkers}
              activeWorkerId={draftWorkerId}
              onActiveWorkerChange={applyWorker}
              daySelectedValues={daySelectedValues}
              numberOfActiveDays={draftDays}
              onNumberOfActiveDaysChange={setDraftDays}
              stepSize={draftStepSize}
              onStepSizeChange={setDraftStepSize}
              startWorkTime={draftStartWorkTime}
              endWorkTime={draftEndWorkTime}
              onStartWorkTimeChange={setDraftStartWorkTime}
              onEndWorkTimeChange={setDraftEndWorkTime}
              searchParams={searchParams}
              hideClosedChairs={draftHideClosedChairs}
              onHideClosedChairsChange={setDraftHideClosedChairs}
              compactClosedDays={draftCompactClosedDays}
              onCompactClosedDaysChange={setDraftCompactClosedDays}
              showTaskIcons={draftShowTaskIcons}
              onShowTaskIconsChange={setDraftShowTaskIcons}
              showTaskTime={draftShowTaskTime}
              onShowTaskTimeChange={setDraftShowTaskTime}
            />
          </div>
          <div className={styles.dropdown__footer}>
            <button type="button" className={styles.save_button} onClick={saveAndClose}>
              {t('common.actions.save')}
            </button>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={dropdownRef} className={styles.customize_selector}>
      <button
        type="button"
        className={headerActionStyles.action_button}
        onClick={toggleDropdown}
        aria-expanded={isDropdownOpen}
        aria-haspopup="dialog"
      >
        <SlidersHorizontal size={18} strokeWidth={2} />
        <span>{title}</span>
      </button>
      {menu}
    </div>
  );
};
